import React, { useState } from 'react';
import Header from '@/components/Header';
import ManufacturerLogos from '@/components/ManufacturerLogos';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, Check, ArrowLeft, Crown, Zap } from 'lucide-react';
import { toast } from 'sonner';
import Footer from '@/components/sections/Footer';
import { useAuth } from '@/contexts/AuthContext';

export default function SellPage() {
  const { token } = useAuth();
  const [step, setStep] = useState(1); // 1 = form, 2 = pricing, 3 = success
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [formData, setFormData] = useState({
    drone_brand: '',
    model_selling: '',
    condition: '',
    drone_category: '',
    asking_price: '',
    currency: 'USD',
    user_type: '',
    seller_email: '',
  });

  const brandOptions = [
    'DJI',
    'Autel Robotics',
    'Wingtra',
    'Freefly',
    'Teledyne FLIR',
    'Parrot',
    'Skydio',
    'Yuneec',
    'IFlight',
    'BetaFPV',
    'Flytrex',
    'Wingcopter',
    'FLIR',
    'Griff Aviation',
    'Harris Aerial',
    'Other',
  ];

  const conditionOptions = [
    'New',
    'Used',
    'Refurbished',
    'For Parts',
  ];

  const categoryOptions = [
    'Inspection',
    'Surveying',
    'Cinematography',
    'Security',
    'Agriculture',
    'Search & Rescue',
    'Racing',
    'Delivery',
    'Thermal Inspection',
    'Heavy Lift',
  ];

  const currencyOptions = [
    'USD',
    'GBP',
    'EUR',
  ];

  const sellerTypeOptions = [
    'Commercial Operator',
    'Enterprise Buyer',
    'Government',
    'Dealer/Reseller',
  ];

  const handleFormSubmit = (e) => {
    e.preventDefault();
    // Move to pricing step
    setStep(2);
  };

  const handlePlanSelect = async (plan) => {
    setSelectedPlan(plan);
    setLoading(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/sell-drone`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          asking_price: parseFloat(formData.asking_price),
          seller_plan: plan, // 'Standard_Single' or 'Dealer_Pro'
          auth_token: token || null,
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        setStep(3);
        toast.success('Listing submitted successfully!');
      } else {
        toast.error('Failed to submit listing. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting listing:', error);
      toast.error('Failed to submit listing. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Success Message
  if (step === 3) {
    const planName = selectedPlan === 'Dealer_Pro' ? 'Dealer Pro ($299/month)' : 'Single Listing ($49)';
    
    return (
      <div className="min-h-screen">
        <Header />
        <ManufacturerLogos />
        <div className="container mx-auto px-4 py-20">
          <Card className="max-w-2xl mx-auto text-center p-12">
            <div className="w-20 h-20 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-accent" />
            </div>
            <h1 className="text-3xl font-bold mb-4">Listing Received!</h1>
            <p className="text-lg text-muted-foreground mb-6">
              An invoice for your <strong>{planName}</strong> plan has been sent to{' '}
              <strong>{formData.seller_email}</strong> (USD).
            </p>
            <div className="bg-muted/50 rounded-lg p-6 mb-8">
              <p className="text-muted-foreground font-medium">
                Your listing will go live once payment is verified.
              </p>
            </div>
            <div className="space-y-4 text-left max-w-md mx-auto">
              <p className="font-semibold">What happens next:</p>
              <ul className="space-y-2 text-muted-foreground">
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                  <span>You'll receive an invoice via email</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                  <span>Once paid, our team verifies your drone details</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                  <span>Your listing goes live within 24-48 hours</span>
                </li>
                {selectedPlan === 'Dealer_Pro' && (
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                    <span>Your dedicated account manager will reach out</span>
                  </li>
                )}
              </ul>
            </div>
            <Button
              size="lg"
              className="mt-8"
              onClick={() => window.location.href = '/'}
            >
              Return to Homepage
            </Button>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  // Step 2: Pricing Plans
  if (step === 2) {
    return (
      <div className="min-h-screen">
        <Header />
        <ManufacturerLogos />

        <section className="bg-gradient-to-b from-muted/50 to-background py-12 sm:py-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <Button 
              variant="ghost" 
              className="mb-6"
              onClick={() => setStep(1)}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Details
            </Button>
            
            <div className="text-center max-w-3xl mx-auto">
              <h1 className="text-4xl sm:text-5xl font-bold mb-4">
                Choose Your Selling Plan
              </h1>
              <p className="text-lg text-muted-foreground">
                Select the plan that best fits your needs
              </p>
            </div>
          </div>
        </section>

        <section className="py-12">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              {/* Listing Summary */}
              <Card className="mb-8 bg-muted/30">
                <CardContent className="pt-6">
                  <h3 className="font-semibold mb-3">Your Listing Summary</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Drone:</span>
                      <p className="font-medium">{formData.drone_brand} {formData.model_selling}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Condition:</span>
                      <p className="font-medium">{formData.condition}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Category:</span>
                      <p className="font-medium">{formData.drone_category}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Price:</span>
                      <p className="font-medium">{formData.currency} {parseFloat(formData.asking_price).toLocaleString()}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Pricing Cards */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Single Listing Card */}
                <Card className="relative border-2 hover:border-primary/50 transition-colors">
                  <CardHeader>
                    <div className="flex items-center gap-2 mb-2">
                      <Zap className="w-5 h-5 text-primary" />
                      <CardTitle>Single Listing</CardTitle>
                    </div>
                    <CardDescription>For Operators</CardDescription>
                    <div className="mt-4">
                      <span className="text-4xl font-bold">$49</span>
                      <span className="text-muted-foreground ml-2">one-time</span>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <p className="text-muted-foreground font-medium">
                      Best for selling 1-2 drones
                    </p>
                    <ul className="space-y-3">
                      <li className="flex items-center gap-2">
                        <Check className="w-5 h-5 text-accent" />
                        <span>Listed until sold</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-5 h-5 text-accent" />
                        <span>Standard Search Ranking</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-5 h-5 text-accent" />
                        <span>Direct Buyer Enquiries</span>
                      </li>
                    </ul>
                    <Button 
                      className="w-full" 
                      size="lg"
                      onClick={() => handlePlanSelect('Standard_Single')}
                      disabled={loading}
                    >
                      {loading && selectedPlan === 'Standard_Single' ? 'Processing...' : 'Pay $49 & List'}
                    </Button>
                  </CardContent>
                </Card>

                {/* Dealer Pro Card */}
                <Card className="relative border-2 border-primary shadow-lg">
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-primary text-primary-foreground px-4 py-1">
                      Recommended
                    </Badge>
                  </div>
                  <CardHeader>
                    <div className="flex items-center gap-2 mb-2">
                      <Crown className="w-5 h-5 text-yellow-500" />
                      <CardTitle>Dealer Pro</CardTitle>
                    </div>
                    <CardDescription>For High Volume</CardDescription>
                    <div className="mt-4">
                      <span className="text-4xl font-bold">$299</span>
                      <span className="text-muted-foreground ml-2">/ month</span>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <p className="text-muted-foreground font-medium">
                      For Resellers & Fleets
                    </p>
                    <ul className="space-y-3">
                      <li className="flex items-center gap-2">
                        <Check className="w-5 h-5 text-accent" />
                        <span>Unlimited Listings</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-5 h-5 text-accent" />
                        <span className="flex items-center gap-1">
                          "Authorized Dealer" Profile Badge
                          <Badge variant="outline" className="ml-1 text-xs bg-yellow-50 text-yellow-700 border-yellow-300">
                            PRO
                          </Badge>
                        </span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-5 h-5 text-accent" />
                        <span>Top of Search Results</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-5 h-5 text-accent" />
                        <span>Dedicated Account Manager</span>
                      </li>
                    </ul>
                    <Button 
                      className="w-full bg-primary hover:bg-primary/90" 
                      size="lg"
                      onClick={() => handlePlanSelect('Dealer_Pro')}
                      disabled={loading}
                    >
                      {loading && selectedPlan === 'Dealer_Pro' ? 'Processing...' : 'Start Pro Subscription'}
                    </Button>
                  </CardContent>
                </Card>
              </div>

              <p className="text-xs text-muted-foreground text-center mt-6">
                All prices in USD. You will receive an invoice via email. Listing goes live after payment verification.
              </p>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    );
  }

  // Step 1: Form
  return (
    <div className="min-h-screen">
      <Header />
      <ManufacturerLogos />

      {/* Hero */}
      <section className="bg-gradient-to-b from-muted/50 to-background py-12 sm:py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl sm:text-5xl font-bold mb-4">
              List Your Hardware
            </h1>
            <p className="text-lg text-muted-foreground">
              Connect with qualified buyers on the world's largest industrial drone marketplace
            </p>
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">Step 1 of 2</Badge>
                </div>
                <CardTitle className="mt-2">Drone Listing Details</CardTitle>
                <CardDescription>
                  Fill out the details below. You'll select your selling plan on the next step.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleFormSubmit} className="space-y-6">
                  {/* Drone Details */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Hardware Details</h3>
                    
                    <div>
                      <Label htmlFor="drone_brand">Manufacturer *</Label>
                      <Select
                        required
                        value={formData.drone_brand}
                        onValueChange={(value) => setFormData({ ...formData, drone_brand: value })}
                      >
                        <SelectTrigger id="drone_brand">
                          <SelectValue placeholder="Select manufacturer" />
                        </SelectTrigger>
                        <SelectContent>
                          {brandOptions.map((option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="model_selling">Model Name (e.g. DJI Matrice 300) *</Label>
                      <Input
                        id="model_selling"
                        required
                        placeholder="Enter drone model"
                        value={formData.model_selling}
                        onChange={(e) => setFormData({ ...formData, model_selling: e.target.value })}
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="condition">Condition *</Label>
                        <Select
                          required
                          value={formData.condition}
                          onValueChange={(value) => setFormData({ ...formData, condition: value })}
                        >
                          <SelectTrigger id="condition">
                            <SelectValue placeholder="Select condition" />
                          </SelectTrigger>
                          <SelectContent>
                            {conditionOptions.map((option) => (
                              <SelectItem key={option} value={option}>
                                {option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label htmlFor="drone_category">Category *</Label>
                        <Select
                          required
                          value={formData.drone_category}
                          onValueChange={(value) => setFormData({ ...formData, drone_category: value })}
                        >
                          <SelectTrigger id="drone_category">
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                          <SelectContent>
                            {categoryOptions.map((option) => (
                              <SelectItem key={option} value={option}>
                                {option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div className="sm:col-span-1">
                        <Label htmlFor="currency">Currency *</Label>
                        <Select
                          required
                          value={formData.currency}
                          onValueChange={(value) => setFormData({ ...formData, currency: value })}
                        >
                          <SelectTrigger id="currency">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            {currencyOptions.map((option) => (
                              <SelectItem key={option} value={option}>
                                {option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="sm:col-span-2">
                        <Label htmlFor="asking_price">Price *</Label>
                        <Input
                          id="asking_price"
                          type="number"
                          required
                          min="0"
                          step="0.01"
                          placeholder="15000"
                          value={formData.asking_price}
                          onChange={(e) => setFormData({ ...formData, asking_price: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Seller Information */}
                  <div className="space-y-4 pt-6 border-t">
                    <h3 className="text-lg font-semibold">Seller Information</h3>
                    
                    <div>
                      <Label htmlFor="seller_email">Email *</Label>
                      <Input
                        id="seller_email"
                        type="email"
                        required
                        placeholder="your@email.com"
                        value={formData.seller_email}
                        onChange={(e) => setFormData({ ...formData, seller_email: e.target.value })}
                      />
                    </div>

                    <div>
                      <Label htmlFor="user_type">Seller Type *</Label>
                      <Select
                        required
                        value={formData.user_type}
                        onValueChange={(value) => setFormData({ ...formData, user_type: value })}
                      >
                        <SelectTrigger id="user_type">
                          <SelectValue placeholder="Select seller type" />
                        </SelectTrigger>
                        <SelectContent>
                          {sellerTypeOptions.map((option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <Button type="submit" size="lg" className="w-full">
                    Continue to Select Plan →
                  </Button>

                  <p className="text-xs text-muted-foreground text-center">
                    By continuing, you agree to our terms and conditions.
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
